<template>
  <ims-modal
    title="Your Profile"
    :show="$store.state.interact.modals.profile"
    :canClose="$User.profile ? true : false"
    :backgroundClose="false"
    @close="$store.dispatch('interact/modals/profile/toggle')"
  >
    <section>
      <ims-tip class="m-2">
        <strong>All fields required.</strong> If your team wins, we will be
        sending prizes to the address provided below.
      </ims-tip>
      <div v-if="!this.$store.state.user.passedBearer">
        <ims-item class="divider">Account</ims-item>
        <ims-item @click="changePassword">Change Password</ims-item>
        <ims-item @click="logout" class="ims-item-last">Logout</ims-item>
      </div>
      <ims-item class="divider">My Profile</ims-item>
      <div class="p-3 bg-white border-bottom ims-input-group">
        <div class="ims-row">
          <ims-input
            type="text"
            :maxlength="30"
            required
            placeholder="First Name"
            v-model="profile.firstName"
          />
          <div class="p-1" />
          <ims-input
            type="text"
            :maxlength="30"
            required
            placeholder="Last Name"
            v-model="profile.lastName"
          />
        </div>
        <ims-input
          type="email"
          :maxlength="30"
          required
          placeholder="Email Address"
          v-model="profile.emailAddress"
        />

        <ims-input
          type="text"
          :maxlength="30"
          :error="!isTeamNameValid"
          required
          placeholder="My Fantasy Team"
          help="3 character minimum"
          v-model="profile.teamName"
          :class="!isFirestone ? 'mb-0' : ''"
        />
        <hr v-if="$store.state.brand.is('firestone')" />
        <ims-input
          v-if="$store.state.brand.is('firestone')"
          type="text"
          :maxlength="8"
          required
          placeholder="Bridgestone UID"
          v-model="profile.firestoneId"
        >
          <template v-slot:right>
            <img
              :src="$Brand.get('firestone').badgeUrl"
              height="40"
              alt="Firestone Badge"
            />
          </template>
        </ims-input>
      </div>
    </section>
    <!-- Location Information -->
    <section class="mb-3">
      <ims-item class="divider">Location</ims-item>
      <div class="p-3 bg-white border-bottom">
        <ims-input
          type="select"
          placeholder="Country"
          v-model="profile.countryId"
        >
          <option
            v-for="(country, index) in countriesSorted"
            :key="index"
            :value="country.id"
            >{{ country.name }}</option
          >
        </ims-input>

        <template v-if="profile.countryId == $AppConfig.usCountryCode">
          <ims-input
            type="text"
            required
            name="address"
            v-model="profile.addressLine1"
            placeholder="Street Address"
          />
          <ims-input
            type="text"
            v-model="profile.addressLine2"
            name="address 2"
            placeholder="Address 2"
          />
          <ims-input
            type="text"
            required
            v-model="profile.city"
            placeholder="City"
          />
          <div class="ims-row">
            <ims-input
              required
              class="filler"
              type="select"
              placeholder="State"
              v-model="profile.stateId"
            >
              <option
                v-for="(state, index) in states"
                :key="index"
                :value="state.id"
                >{{ state.name }}</option
              >
            </ims-input>
            <div class="p-1" />
            <ims-input
              class="filler"
              required
              type="text"
              v-model="profile.postalCode"
              placeholder="Postal Code"
            />
          </div>
        </template>
        <div class="pt-3 text-center" v-else>
          <ims-icon icon="error" size="md" color="red" class="mb-2" />
          <p class="text-sm text-uppercase">
            Non-US residents are invited to play {{ $store.state.brand.name }}
            {{ $store.state.brand.gameName }}, but are ineligible for weekly and
            season-long prizes.
          </p>
        </div>
      </div>
    </section>
    <div class="p-3"></div>
    <template v-slot:footer>
      <ims-toolbar background="transparent">
        <ims-button
          :disabled="!canSave || saving"
          type="solid"
          color="blue"
          size="md"
          class="btn-block"
          @click="saveProfile"
        >
          <span v-if="saving">Saving...</span>
          <span v-else>Save Profile</span>
        </ims-button>
      </ims-toolbar>
    </template>
  </ims-modal>
</template>

<script>
import Profile from '@/classes/profile';
import AppConfig from '@/AppConfig';
import isEmail from '@/modules/validators/email';

export default {
  data() {
    return {
      countries: [],
      states: [],
      profile: new Profile({}),
      saving: false
    };
  },
  watch: {
    async '$store.state.interact.modals.profile'(shouldShow) {
      if (shouldShow) {
        // Set Doc Title
        this.$store.dispatch('document/title', 'My Profile');
        // Load Profile
        this.profile = new Profile(this.$User.profile);
        // Load Email from Auth
        if (!this.profile.emailAddress) {
          // Removing for now TODO: see why no email
          if ((this.$User.account.emails || []).length) {
            this.profile.emailAddress = this.$User.account.emails[0];
          }
        }
        // Set Country to US if its not set
        if (!this.profile.countryId) {
          this.profile.countryId = AppConfig.usCountryCode;
        }
        // Set first name to account name if not set
        // Get Countries & States
        this.countries = await this.$User.service.getCountries();
        this.states = await this.$User.service.getStates();
      } else {
        this.$store.dispatch('document/title/reset');
      }
    }
  },
  computed: {
    isTeamNameValid() {
      if (this.profile) {
        return ((this.profile || {}).teamName || '').length >= 3;
      } else {
        return false;
      }
    },

    isFirestone() {
      return this.$store.state.brand.is('firestone');
    },
    countriesSorted() {
      return (
        this.countries ||
        [].sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        })
      );
    },
    isEmailValid() {
      return isEmail(this.profile.emailAddress);
    },
    canSave() {
      // Are the base required fields complete?
      // (this.profile.playerName || '').length >= 3 &&
      let fieldsComplete =
        this.profile.firstName &&
        this.profile.lastName &&
        isEmail(this.profile.emailAddress) &&
        (this.profile.teamName || '').length >= 3 &&
        this.profile.countryId;

      // If it's a US country code and not firestone
      if (
        this.profile.countryId == AppConfig.usCountryCode &&
        !this.$store.state.brand.is('firestone')
      ) {
        return (
          fieldsComplete &&
          this.profile.stateId &&
          this.profile.city &&
          this.profile.postalCode
        );

        // If it's use Country and Firestone
      } else if (
        this.profile.countryId == AppConfig.usCountryCode &&
        this.$store.state.brand.is('firestone')
      ) {
        return (
          fieldsComplete &&
          this.profile.stateId &&
          this.profile.city &&
          this.profile.postalCode &&
          this.profile.firestoneId
        );

        // Else if its just firestone and no US
      } else if (this.$store.state.brand.is('firestone')) {
        // Require a firestone id be longer than 6 and less than 9
        // FUTURE TODO - this hardcoded stuff for the brand should go
        // potentially move conditionals as regex to brand.json
        return (
          (this.profile.firestoneId || '').length < 9 &&
          (this.profile.firestoneId || '').length > 6
        );
      } else {
        return fieldsComplete;
      }
    }
  },
  methods: {
    logout() {
      this.$store.dispatch('interact/modals/profile/toggle'); // hide
      this.$store.dispatch('user/logout');
    },
    getName(fullname) {
      let output = {};
      let nameArray = fullname || ''.split(' ');

      if (nameArray.length > 2) {
        output.lastname = nameArray.pop();
        output.firstname = nameArray.join(' ');
      } else if (nameArray.length > 0) {
        output.lastname =
          nameArray.length > 1 ? nameArray[nameArray.length - 1] : '';
        output.firstname = nameArray[0];
      }

      return output;
    },
    async changePassword() {
      try {
        window.location.href = `${window.location.origin}${AppConfig.sso.forgotPassword}`;
      } catch (e) {
        this.$store.dispatch('error', e.message);
      }
    },
    async saveProfile() {
      let closeLoader = await this.$store.dispatch(
        'loader/start',
        'Saving Profile...'
      );
      this.saving = true;

      // Save Profile
      let payload = this.profile.toObj();
      delete payload.id;

      try {
        let response = await this.$User.service.saveProfile(payload);
        // Did it save
        if (response) {
          // Update User Profile
          this.$User.profile = this.profile;
          // Fire off Toast
          this.$store.dispatch('interact/toast', { title: 'Profile Saved' }); // toast
          this.$store.dispatch('interact/modals/profile/toggle'); // hide
        } else {
          this.$store.dispatch(
            'interact/error',
            'Service response was empty. Error unknown'
          );
        }
      } catch (e) {
        this.$store.dispatch('interact/error', e.message);
      }
      this.saving = false;
      closeLoader();
    }
  },
  async mounted() {}
};
</script>

<style></style>
